import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCreateTaskMutation, useUpdateTaskMutation } from '../../api/platformApi';
import { apiErrorMessage } from '../../utils/apiError';
import { PRIORITIES } from './taskConstants';

// 'yyyy-MM-ddTHH:mm' for <input type="datetime-local">, rendered in the browser's local clock.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function TaskComposerModal({ task, onClose, onSaved }) {
  const editing = !!task?.taskId;
  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: updating }] = useUpdateTaskMutation();

  const [title, setTitle] = useState(task?.title ?? '');
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [dueAt, setDueAt] = useState(toLocalInput(task?.dueAt) || toLocalInput(defaultDue()));
  const [priority, setPriority] = useState(task?.priority ?? 'Normal');
  const saving = creating || updating;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Give the task a title.'); return; }

    const body = {
      title: title.trim(),
      notes: notes.trim() || null,
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      priority,
    };

    try {
      if (editing) await updateTask({ id: task.taskId, ...body }).unwrap();
      else await createTask({ ...body, patientId: task?.patientId, leadId: task?.leadId }).unwrap();
      toast.success(editing ? 'Task updated' : 'Task added');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h2>{editing ? 'Edit task' : 'New task'}</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="task-title">Title *</label>
              <input
                id="task-title"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Call Mrs Sharma about her renewal"
                autoFocus
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="task-due">Due</label>
                <input
                  id="task-due"
                  type="datetime-local"
                  className="form-control"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="task-priority">Priority</label>
                <select
                  id="task-priority"
                  className="form-control"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="task-notes">Notes</label>
              <textarea
                id="task-notes"
                className="form-control"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything the next person picking this up needs to know"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** New tasks default to 10:00 today — a working hour, not "right now". */
function defaultDue() {
  const d = new Date();
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}
